import React, { useState } from 'react';
import { Tag, Space, Progress, Tooltip } from 'antd';
import { LineChartOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
} from 'recharts';

const getTrendData = (report, type) => {
    const trend = report.shoppingTrends?.[`${type}Trend`]?.results?.[0]?.data;
    if (!trend || trend.length === 0) return null;
    const lastPeriod = trend[trend.length - 1].period;
    return trend.filter(d => d.period === lastPeriod);
};

const getGenderRatio = report => {
    const items = getTrendData(report, 'gender');
    if (!items) return { female: 54, male: 46 };
    const f = items.find(d => d.group === 'f')?.ratio || 0;
    const m = items.find(d => d.group === 'm')?.ratio || 0;
    const total = f + m || 1;
    return { female: Math.round((f / total) * 100), male: Math.round((m / total) * 100) };
};

const getDeviceRatio = report => {
    const items = getTrendData(report, 'device');
    if (!items) return { mobile: 83, pc: 17 };
    const m = items.find(d => d.group === 'mo')?.ratio || 0;
    const p = items.find(d => d.group === 'pc')?.ratio || 0;
    const total = m + p || 1;
    return { mobile: Math.round((m / total) * 100), pc: Math.round((p / total) * 100) };
};

const getAgeRatio = report => {
    const items = getTrendData(report, 'age');
    const labels = { 10: '10대', 20: '20대', 30: '30대', 40: '40대', 50: '50대', 60: '60대' };
    if (!items) return Object.values(labels).map(l => ({ label: l, ratio: 0 }));
    return Object.keys(labels).map(key => {
        const item = items.find(d => d.group === key);
        return { label: labels[key], ratio: Math.round(item?.ratio || 0) };
    });
};

const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div
            style={{
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                padding: '8px 12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}
        >
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#4f46e5' }}>
                {payload[0]?.value?.toFixed(1)}
            </div>
        </div>
    );
};

const filterByUnit = (data, unit) => {
    if (!data || !data.length) return [];
    if (unit === '6개월') return data.slice(-6);
    if (unit === '3개월') return data.slice(-3);
    return data;
};

const getStatusTag = status => {
    if (status === '좋음')
        return (
            <Tag color="success" style={{ fontSize: 13, padding: '2px 8px' }}>
                좋음
            </Tag>
        );
    if (status === '위험')
        return (
            <Tag color="error" style={{ fontSize: 13, padding: '2px 8px' }}>
                위험
            </Tag>
        );
    return (
        <Tag color="warning" style={{ fontSize: 13, padding: '2px 8px' }}>
            주의
        </Tag>
    );
};

const AGE_COLORS = ['#cbd5e1', '#94a3b8', '#64748b', '#4f46e5', '#7c3aed', '#1e293b'];

const MarketTrendSection = ({ report }) => {
    const [unit, setUnit] = useState('1년');

    const gender = getGenderRatio(report);
    const device = getDeviceRatio(report);
    const ages = getAgeRatio(report);

    const rawTrend = report?.shoppingTrends?.keywordTrend?.results?.[0]?.data;
    const hasRealData = rawTrend && rawTrend.length > 0;
    const chartData = hasRealData
        ? filterByUnit(
              rawTrend.map(d => ({
                  date: d.period?.slice(0, 7),
                  ratio: Math.round(d.ratio * 10) / 10,
              })),
              unit
          )
        : [];

    const sa = report?.seasonalityAnalysis;
    const monthlySearchVolume = (report?.keywordInfo?.total || 0).toLocaleString();
    const units = ['3개월', '6개월', '1년'];

    return (
        <>
            <div className="section-title-premium">
                <LineChartOutlined /> 키워드 시장성 및 트렌드
            </div>

            {/* 상단: 검색량 추이 차트(좌) + 기기/성별/연령대(우) */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 16 }}>
                {/* 검색량 추이 차트 */}
                <div className="diag-card">
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 20,
                        }}
                    >
                        <Space>
                            <span className="card-sub-title" style={{ marginBottom: 0 }}>
                                검색량 추이
                            </span>
                            {!hasRealData && (
                                <Tag color="orange" style={{ fontSize: 10 }}>
                                    샘플 차트
                                </Tag>
                            )}
                        </Space>
                        <Space>
                            {units.map(u => (
                                <button
                                    key={u}
                                    onClick={() => setUnit(u)}
                                    style={{
                                        padding: '3px 10px',
                                        borderRadius: 6,
                                        border: '1px solid',
                                        fontSize: 12,
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        background: unit === u ? '#4f46e5' : 'white',
                                        color: unit === u ? 'white' : '#64748b',
                                        borderColor: unit === u ? '#4f46e5' : '#e2e8f0',
                                    }}
                                >
                                    {u}
                                </button>
                            ))}
                        </Space>
                    </div>

                    {hasRealData ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <AreaChart
                                data={chartData}
                                margin={{ top: 8, right: 8, left: -20, bottom: 0 }}
                            >
                                <defs>
                                    <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.2} />
                                        <stop offset="95%" stopColor="#4f46e5" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="#f1f5f9"
                                    vertical={false}
                                />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 10, fill: '#94a3b8' }}
                                    axisLine={false}
                                    tickLine={false}
                                    interval="preserveStartEnd"
                                />
                                <YAxis
                                    tick={{ fontSize: 10, fill: '#94a3b8' }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <RechartsTooltip content={<ChartTooltip />} />
                                <Area
                                    type="monotone"
                                    dataKey="ratio"
                                    stroke="#4f46e5"
                                    strokeWidth={2.5}
                                    fill="url(#trendGradient)"
                                    dot={false}
                                    activeDot={{
                                        r: 4,
                                        fill: '#4f46e5',
                                        stroke: 'white',
                                        strokeWidth: 2,
                                    }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <div style={{ height: 220 }}>
                            <svg viewBox="0 0 800 220" className="modern-chart-svg">
                                <defs>
                                    <linearGradient id="colorSearch" x1="0" y1="0" x2="0" y2="1">
                                        <stop
                                            offset="5%"
                                            stopColor="var(--accent-blue)"
                                            stopOpacity={0.2}
                                        />
                                        <stop
                                            offset="95%"
                                            stopColor="var(--accent-blue)"
                                            stopOpacity={0}
                                        />
                                    </linearGradient>
                                </defs>
                                <path
                                    d="M0,150 Q50,160 100,155 T200,140 T300,100 T400,80 T500,160 T600,165 L600,220 L0,220 Z"
                                    fill="url(#colorSearch)"
                                />
                                <path
                                    d="M0,150 Q50,160 100,155 T200,140 T300,100 T400,80 T500,160 T600,165"
                                    fill="none"
                                    stroke="var(--accent-blue)"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                    )}
                </div>

                {/* 우측: 기기/성별/연령대 비중 세로 배치 */}
                <div
                    className="diag-card"
                    style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                >
                    {/* 기기 비중 */}
                    <div className="demo-bar-item" style={{ marginBottom: 16 }}>
                        <div className="demo-bar-header" style={{ marginBottom: 8 }}>
                            <span>기기 비중</span>
                            <span style={{ color: '#4f46e5', fontWeight: 700 }}>
                                모바일 {device.mobile}%
                            </span>
                            <span style={{ color: '#94a3b8' }}>PC {device.pc}%</span>
                        </div>
                        <Progress
                            percent={device.mobile}
                            strokeColor="#4f46e5"
                            trailColor="#e2e8f0"
                            showInfo={false}
                            size={[null, 14]}
                        />
                    </div>
                    {/* 성별 비중 */}
                    <div className="demo-bar-item" style={{ marginBottom: 16 }}>
                        <div className="demo-bar-header" style={{ marginBottom: 8 }}>
                            <span>성별 비중</span>
                            <span style={{ color: '#ec4899', fontWeight: 700 }}>
                                여성 {gender.female}%
                            </span>
                            <span style={{ color: '#3b82f6' }}>남성 {gender.male}%</span>
                        </div>
                        <Progress
                            percent={gender.female}
                            strokeColor="#ec4899"
                            trailColor="#3b82f6"
                            showInfo={false}
                            size={[null, 14]}
                        />
                    </div>
                    {/* 연령대 미니 바 */}
                    <div className="demo-bar-item">
                        <div
                            style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: '#475569',
                                marginBottom: 8,
                            }}
                        >
                            연령대 비중
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {ages.map((a, idx) => (
                                <div
                                    key={idx}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                                >
                                    <span
                                        style={{
                                            width: 32,
                                            fontSize: 12,
                                            color: '#64748b',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {a.label}
                                    </span>
                                    <div
                                        style={{
                                            flex: 1,
                                            background: '#f1f5f9',
                                            borderRadius: 4,
                                            height: 8,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: `${a.ratio > 0 ? Math.max(a.ratio, 2) : 0}%`,
                                                background: AGE_COLORS[idx],
                                                height: '100%',
                                                borderRadius: 4,
                                            }}
                                        />
                                    </div>
                                    <span
                                        style={{
                                            width: 34,
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: '#334155',
                                            textAlign: 'right',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {a.ratio}%
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* 하단: 시즌성 분석 카드 full width */}
            <div className="diag-card" style={{ marginTop: 16 }}>
                {sa ? (
                    <>
                        {/* 헤더 */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 18,
                                flexWrap: 'wrap',
                            }}
                        >
                            {getStatusTag(sa.aiAnalysis?.status)}
                            <span style={{ fontSize: 15, fontWeight: 700, color: '#4f46e5' }}>
                                시즌성 {sa.seasonalityScore}점
                            </span>
                            <Tooltip
                                title={
                                    <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                                        <b>시즌성 점수 계산 기준</b>
                                        <br />
                                        네이버 DataLab 검색 트렌드 지수(0~100 상대값)의
                                        <br />
                                        변동계수(CV = 표준편차 ÷ 평균 × 100) 기반 산출
                                        <br />
                                        · 0~30점: 연중 고른 수요
                                        <br />
                                        · 30~60점: 시즌성 있음
                                        <br />· 60점 이상: 특정 시즌에 집중
                                    </div>
                                }
                            >
                                <QuestionCircleOutlined
                                    style={{ color: '#94a3b8', cursor: 'help', fontSize: 14 }}
                                />
                            </Tooltip>
                            {sa.recentChange !== 0 && (
                                <>
                                    <span style={{ color: '#e2e8f0' }}>|</span>
                                    <span
                                        style={{
                                            fontSize: 14,
                                            fontWeight: 700,
                                            color: sa.recentChange > 0 ? '#ef4444' : '#3b82f6',
                                        }}
                                    >
                                        {sa.recentChange > 0
                                            ? `+${sa.recentChange}%`
                                            : `${sa.recentChange}%`}{' '}
                                        {sa.recentChange > 0 ? '상승' : '하락'}
                                    </span>
                                    <Tooltip title="이전 달 대비 최근 달의 검색 트렌드 지수 변화율 (DataLab 상대값 기준)">
                                        <QuestionCircleOutlined
                                            style={{
                                                color: '#94a3b8',
                                                cursor: 'help',
                                                fontSize: 14,
                                            }}
                                        />
                                    </Tooltip>
                                </>
                            )}
                        </div>

                        {/* 수치 3칸 */}
                        <div className="season-data-grid" style={{ marginBottom: 14 }}>
                            <div className="season-data-box">
                                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                                    피크 월
                                </div>
                                <div style={{ fontSize: 22, fontWeight: 800, color: '#10b981' }}>
                                    {sa.peakMonth}월
                                </div>
                            </div>
                            <div className="season-data-box">
                                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                                    최근 변화
                                </div>
                                <div
                                    style={{
                                        fontSize: 22,
                                        fontWeight: 800,
                                        color: sa.recentChange > 0 ? '#ef4444' : '#3b82f6',
                                    }}
                                >
                                    {sa.recentChange > 0
                                        ? `+${sa.recentChange}%`
                                        : `${sa.recentChange}%`}
                                </div>
                            </div>
                            <div className="season-data-box">
                                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                                    월 검색량
                                </div>
                                <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>
                                    {monthlySearchVolume}
                                </div>
                            </div>
                        </div>

                        {/* 상승/하락 월 */}
                        <div style={{ display: 'flex', gap: 12, marginBottom: 14, fontSize: 13 }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <span style={{ color: '#64748b', fontWeight: 600, flexShrink: 0 }}>
                                    상승
                                </span>
                                {sa.risingMonths?.map(m => (
                                    <Tag key={m} color="green" style={{ margin: 0 }}>
                                        {m}월
                                    </Tag>
                                ))}
                            </div>
                            <span style={{ color: '#e2e8f0' }}>|</span>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <span style={{ color: '#64748b', fontWeight: 600, flexShrink: 0 }}>
                                    하락
                                </span>
                                {sa.fallingMonths?.map(m => (
                                    <Tag key={m} color="error" style={{ margin: 0 }}>
                                        {m}월
                                    </Tag>
                                ))}
                            </div>
                        </div>

                        {/* AI 분석 텍스트 */}
                        {sa.aiAnalysis && (
                            <div style={{ background: '#f8fafc', padding: 14, borderRadius: 12 }}>
                                <div
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 800,
                                        color: '#0f172a',
                                        marginBottom: 6,
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {sa.aiAnalysis.summary}
                                </div>
                                <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5 }}>
                                    💡 {sa.aiAnalysis.advice}
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    /* 샘플 fallback */
                    <>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                            <Tag color="warning" style={{ fontSize: 13, padding: '2px 8px' }}>
                                주의
                            </Tag>
                            <span style={{ fontSize: 15, fontWeight: 700, color: '#4f46e5' }}>
                                시즌성 28점
                            </span>
                            <Tag color="orange" style={{ fontSize: 10 }}>
                                샘플
                            </Tag>
                            <span style={{ fontSize: 15, color: '#f59e0b', fontWeight: 700 }}>
                                -29.8% 하락
                            </span>
                        </div>
                        <div className="season-data-grid">
                            <div className="season-data-box">
                                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>
                                    피크 월
                                </div>
                                <div style={{ fontSize: 20, fontWeight: 800, color: '#10b981' }}>
                                    12월
                                </div>
                            </div>
                            <div className="season-data-box">
                                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>
                                    다음 달
                                </div>
                                <div style={{ fontSize: 20, fontWeight: 800, color: '#ef4444' }}>
                                    -27%
                                </div>
                            </div>
                            <div className="season-data-box">
                                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>
                                    월 검색량
                                </div>
                                <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
                                    30,090
                                </div>
                            </div>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: 13,
                                color: '#475569',
                                marginBottom: 12,
                            }}
                        >
                            <Space>
                                <span>상승</span>
                                <Tag color="green">2월</Tag>
                                <Tag color="green">12월</Tag>
                            </Space>
                            <Space>
                                <span>하락</span>
                                <Tag color="error">6월</Tag>
                                <Tag color="error">8월</Tag>
                            </Space>
                        </div>
                        <div style={{ background: '#f8fafc', padding: 16, borderRadius: 12 }}>
                            <div
                                style={{
                                    fontSize: 14,
                                    fontWeight: 800,
                                    color: '#0f172a',
                                    marginBottom: 6,
                                }}
                            >
                                수요가 감소하는 하락기입니다. 선제적 전략 전환이 필요합니다.
                            </div>
                            <div style={{ fontSize: 13, color: '#475569' }}>
                                💡 타켓 키워드를 재정비하고 전환율 높은 롱테일 키워드 광고에
                                집중하세요.
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default MarketTrendSection;
